import {
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { debounceTime } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';

import { AvatarComponent } from '../../common/avatar/avatar.component';
import { ChipsComponent } from '../../common/chips/chips.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { ProtectedControlDirective } from '../protected-controls/protected-control.directive';
import { Select2EmptyStateDirective } from '../select2/default-templates/empty-state/empty-state.directive';
import { Select2ListItemDirective } from '../select2/default-templates/list-item/list-item.directive';
import { Select2SelectedItemDirective } from '../select2/default-templates/selected-item/selected-item.directive';
import { MultiselectFeatureDirective } from '../select2/features/multiselect/multiselect-feature.directive';
import { SelectedItemCounterDirective } from '../select2/features/multiselect/selected-item-counter/selected-item-counter.directive';
import { MultiSelectTriggerComponent } from '../select2/features/multiselect/trigger/trigger-default.component';
import { SearchFeatureDirective } from '../select2/features/search/search-feature.directive';
import { Select2Component } from '../select2/select2.component';
import { Select2Item } from '../select2/types';

export interface BasicMultiSelectOption extends Select2Item {
  photoThumbUrl?: string;
  hasAvatar?: boolean;
  initials?: string;
}

@Component({
  selector: 'rt-basic-multi-select',
  imports: [
    Select2Component,
    DropdownItemComponent,
    Select2ListItemDirective,
    MultiSelectTriggerComponent,
    ChipsComponent,
    Select2SelectedItemDirective,
    MultiselectFeatureDirective,
    SelectedItemCounterDirective,
    CheckboxComponent,
    ProtectedControlDirective,
    ReactiveFormsModule,
    TranslatePipe,
    SearchFeatureDirective,
    AvatarComponent,
    Select2EmptyStateDirective,
  ],
  templateUrl: './basic-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './basic-multi-select.component.less',
})
export class BasicMultiSelectComponent {
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  public control = input.required<FormControl<string[] | number[]>>();
  public selectOptions = input.required<BasicMultiSelectOption[]>();
  public preselectedOptions = input<BasicMultiSelectOption[]>([]);
  public isInvalid = input<boolean>(false);
  public isReadOnly = input<boolean>(false);
  public disableLocalSearch = input<boolean>(false);
  public hasExtendedPreview = input<boolean>(false);
  public noResultsMessage = input(this.i18n.translate('app.common.no_results_found'));

  public searchControl = new FormControl('', { nonNullable: true });
  public selectedItems = signal<BasicMultiSelectOption[]>([]);

  public search = output<string>();
  public scrollChange = output<IPageInfo>();

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(query => {
        this.search.emit(query);
      });
    this.selectedItems.set(this.preselectedOptions() || []);
  }

  public onSelectChange(selected: BasicMultiSelectOption[]): void {
    this.selectedItems.set(selected);
  }
}
